import { useQuery } from "@tanstack/vue-query";
import { usePageSettingsClient } from "~/layers/page-settings/composables/usePageSettingsClient";
import { PAGE_SETTINGS_DEFAULTS } from "~/layers/page-settings/config";

export const useWebsiteContent = () => {
  const currentRoute = useRoute();

  const { getWebsiteGamesDataQueryOptions } = useGamesRepositoryClient();
  const pageSettingsClient = usePageSettingsClient();
  const careersClient = useCareersClient();
  const generalSettingsClient = useGeneralSettingsClient();
  const newsClient = useNewsClient();
  const socialMediaClient = useSocialMediaClient();

  function getGeneralSettings() {
    return useQuery({
      queryKey: ["website", "general", "settings"],
      queryFn: () => {
        return generalSettingsClient.get();
      },
    });
  }

  function getAllPagesSettings() {
    return useQuery({
      queryKey: ["website", "pages", "settings"],
      queryFn: () => {
        return pageSettingsClient.getAllPagesSettings();
      },
    });
  }

  function getGlobalPageSettings() {
    return useQuery({
      queryKey: ["website", "pages", "settings", "global"],
      queryFn: () => {
        return pageSettingsClient.getGlobalPageSettings();
      },
    });
  }

  function getCurrentPageSettings() {
    const allPagesSettingsQuery = getAllPagesSettings();
    const globalPageSettingsQuery = getGlobalPageSettings();

    const currentPageSettings = computed(() =>
      allPagesSettingsQuery.data.value?.find((page) =>
        normalizeUrlPath(page.path).startsWith(
          normalizeUrlPath(currentRoute.path)
        )
      )
    );

    const data = computed(() => ({
      cover: {
        backgroundUri:
          currentPageSettings.value?.cover.backgroundUri ||
          currentRoute.meta.settings?.cover?.backgroundUri ||
          globalPageSettingsQuery.data.value?.cover.backgroundUri ||
          PAGE_SETTINGS_DEFAULTS.cover.backgroundUri,
        title:
          currentPageSettings.value?.cover.title ??
          currentRoute.meta.settings?.cover?.title ??
          PAGE_SETTINGS_DEFAULTS.cover.title,
      },
      meta: {
        title:
          currentPageSettings.value?.meta.title ||
          currentRoute.meta.settings?.meta?.title ||
          globalPageSettingsQuery.data.value?.meta.title ||
          PAGE_SETTINGS_DEFAULTS.meta.title,
        description:
          currentPageSettings.value?.meta.description ||
          currentRoute.meta.settings?.meta?.description ||
          globalPageSettingsQuery.data.value?.meta.description ||
          PAGE_SETTINGS_DEFAULTS.meta.description,
        keywords:
          currentPageSettings.value?.meta.keywords ||
          currentRoute.meta.settings?.meta?.keywords ||
          globalPageSettingsQuery.data.value?.meta.keywords ||
          PAGE_SETTINGS_DEFAULTS.meta.keywords,
        ogImage:
          currentPageSettings.value?.meta.ogImage ||
          currentRoute.meta.settings?.meta?.ogImage ||
          globalPageSettingsQuery.data.value?.meta.ogImage ||
          PAGE_SETTINGS_DEFAULTS.meta.ogImage,
        favicon:
          currentPageSettings.value?.meta.favicon ||
          currentRoute.meta.settings?.meta?.favicon ||
          globalPageSettingsQuery.data.value?.meta.favicon ||
          PAGE_SETTINGS_DEFAULTS.meta.favicon,
      },
      additional:
        currentPageSettings.value?.additional ??
        currentRoute.meta.settings?.additional ??
        {},
    }));

    return {
      data,
      suspense: async () => {
        await Promise.all([
          allPagesSettingsQuery.suspense(),
          globalPageSettingsQuery.suspense(),
        ]);
        return data.value;
      },
    };
  }

  function getAllJobs() {
    return useQuery({
      queryKey: ["website", "jobs"],
      queryFn: async () => {
        return careersClient.findOpened();
      },
    });
  }

  function getJobById(id: MaybeRefOrGetter<number>) {
    const allJobsQuery = getAllJobs();
    const idValue = toValue(id);
    return {
      ...allJobsQuery,
      data: computed(
        () => allJobsQuery.data.value?.find((job) => job.id === idValue) ?? null
      ),
    };
  }

  function getAllNews() {
    return useQuery({
      queryKey: ["website", "news"],
      queryFn: () => {
        return newsClient.findPublished();
      },
    });
  }

  function getNewsArticleBySlug(slug: MaybeRefOrGetter<string>) {
    const allNewsQuery = getAllNews();
    const slugValue = toValue(slug);
    return {
      ...allNewsQuery,
      data: computed(
        () =>
          allNewsQuery.data.value?.data.find((news) => news.url === slugValue) ??
          null
      ),
    };
  }

  function getCompanyGames() {
    return useQuery({
      ...getWebsiteGamesDataQueryOptions(),
      staleTime: 1000 * 60, // 1 minute
      refetchInterval: 1000 * 60, // 1 minute
    });
  }

  function getVariables() {
    const {
      data: gamesData,
      suspense: gamesDataSuspense,
      refetch: gamesDataRefetch,
    } = getCompanyGames();
    const {
      data: generalData,
      suspense: generalDataSuspense,
      refetch: generalDataRefetch,
    } = getGeneralSettings();

    const data = computed(() => ({
      totalVisits: gamesData.value?.statsSummary.visits ?? 0,
      totalPlayersOnline: gamesData.value?.statsSummary.playersOnline ?? 0,
      teamMembers: generalData.value?.teamMembers ?? 0,
      gamesLaunched: generalData.value?.gamesLaunched ?? 0,
      monthlyActiveUsers: generalData.value?.monthlyActiveUsers ?? 0,
      monthlyVisits: gamesData.value?.statsSummary.lastMonthVisits ?? 0,
    }));

    return {
      data,
      suspense: async () => {
        await Promise.all([gamesDataSuspense(), generalDataSuspense()]);
        return data.value;
      },
      refetch: async () => {
        await Promise.all([gamesDataRefetch(), generalDataRefetch()]);
        return data.value;
      },
    };
  }

  function getSocialMedia() {
    return useQuery({
      queryKey: ["website", "social-media"],
      queryFn: () => {
        return socialMediaClient.findAll();
      },
    });
  }

  return {
    getGeneralSettings,
    getCurrentPageSettings,
    getAllJobs,
    getJobById,
    getAllNews,
    getNewsArticleBySlug,
    getAllPagesSettings,
    getGlobalPageSettings,
    getCompanyGames,
    getVariables,
    getSocialMedia,
  };
};
